import { useState } from "react";

import useDialogStore from "@store/useDialogStore";
import type { IFeedbackOption } from "@interfaces/thread.interface";
import { Checkbox } from "@ui/FormElements/Checkbox";
import { BaseDialog } from "@ui/Dialogs/BaseDialog";

interface FeedbackDialogProps {
  options: IFeedbackOption[];
  onSubmit: (reasons: string[], comment: string) => void;
}

export function FeedbackDialog({ options, onSubmit }: FeedbackDialogProps) {
  const { closeDialog } = useDialogStore();
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const hasOther = selectedReasons.includes("other");

  const handleToggleReason = (code: string) => {
    setSelectedReasons((prev) =>
      prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code]
    );
  };

  const handleSubmit = () => {
    onSubmit(selectedReasons, comment);
    // Don't call closeDialog() here — the onSubmit callback swaps to ThankYouDialog via openDialog
  };

  return (
    <BaseDialog
      title="What was missing or incorrect?"
      titleClassName="text-base"
      showCloseButton={false}
      primaryButton={{
        label: "Submit feedback",
        callback: handleSubmit,
        disabled: selectedReasons.length === 0,
      }}
      secondaryButton={{
        label: "Cancel",
        callback: closeDialog,
      }}
    >
      <div className="flex flex-col gap-3 px-4 pb-2">
        {options.map((option) => (
          <Checkbox
            key={option.code}
            id={`feedback-${option.code}`}
            label={option.label}
            isChecked={selectedReasons.includes(option.code)}
            onChange={() => handleToggleReason(option.code)}
          />
        ))}

        {hasOther && (
          <textarea
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-3 text-sm text-gray-700 dark:text-invertedPrimary80 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-400 resize-none"
            rows={3}
            placeholder="Let us know what didn't work well in this thread context."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        )}
      </div>
    </BaseDialog>
  );
}
